import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "../../ds2/components";
import {
  selectUser,
  selectAdminAuth,
  setAdminAuth,
} from "../../redux/features/appData";
import { apiVerifyAdminSecret } from "../../services/AdminService";
import { apiGetAllThreads, Thread } from "../../services/ThreadService";
import { ThreadsTable } from "./ThreadsTable";
import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";
import "./AdminThreadsPage.css";

export const AdminThreadsPage: React.FC = () => {
  const dispatch = useDispatch();
  const storeUser = useSelector(selectUser);
  const adminAuth = useSelector(selectAdminAuth);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [adminSecret, setAdminSecret] = useState<string>("");
  const [verifying, setVerifying] = useState<boolean>(false);

  const handleAdminVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError(null);

    try {
      const verified = await apiVerifyAdminSecret(adminSecret);
      if (verified) {
        dispatch(
          setAdminAuth({
            isAuthenticated: true,
            adminSecret: adminSecret,
            verifiedAt: new Date(),
          })
        );
        setAdminSecret("");
      } else {
        setError("Invalid admin secret");
      }
    } catch (err) {
      setError("Failed to verify admin secret");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (!adminAuth.isAuthenticated) {
      setLoading(false);
      return;
    }
    
    const fetchThreads = async () => {
      if (!storeUser?.currentWorkspaceId) {
        setError("No workspace selected");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const threadsData = await apiGetAllThreads(
          storeUser.currentWorkspaceId,
          adminAuth.adminSecret
        );
        if (threadsData) {
          setThreads(threadsData);
        } else {
          setThreads([]);
        }
      } catch (err) {
        console.error("Error fetching threads:", err);
        setError("Failed to load threads");
      } finally {
        setLoading(false);
      }
    };

    fetchThreads();
  }, [storeUser?.currentWorkspaceId, adminAuth.isAuthenticated, adminAuth.adminSecret]);

  if (!adminAuth.isAuthenticated) {
    return (
      <Theme>
        <div className="admin-threads-page">
          <div className="admin-threads-page-header">
            <h1>Admin Access Required</h1>
          </div>
          <div className="admin-threads-page-content">
            <form
              onSubmit={handleAdminVerification}
              className="admin-auth-form">
              <div className="admin-auth-field">
                <label htmlFor="adminSecret">Admin Secret:</label>
                <input
                  type="password"
                  id="adminSecret"
                  value={adminSecret}
                  onChange={(e) => setAdminSecret(e.target.value)}
                  placeholder="Enter admin secret"
                  required
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                disabled={verifying}>
                {verifying ? "Verifying..." : "Access Admin Panel"}
              </Button>
            </form>
            {error && <div className="admin-threads-page-error">{error}</div>}
          </div>
        </div>
      </Theme>
    );
  }

  if (loading) {
    return (
      <Theme>
        <div className="admin-threads-page">
          <div className="admin-threads-page-header">
            <h1>Threads</h1>
          </div>
          <div className="admin-threads-page-loading">Loading threads...</div>
        </div>
      </Theme>
    );
  }

  if (error) {
    return (
      <Theme>
        <div className="admin-threads-page">
          <div className="admin-threads-page-header">
            <h1>Threads</h1>
          </div>
          <div className="admin-threads-page-error">{error}</div>
        </div>
      </Theme>
    );
  }

  return (
    <Theme>
      <div className="admin-threads-page">
        <div className="admin-threads-page-header">
          <h1>Threads</h1>
          <div className="admin-threads-page-stats">
            {threads.length} thread{threads.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="admin-threads-page-content">
          <ThreadsTable threads={threads} adminSecret={adminAuth.adminSecret} />
        </div>
      </div>
    </Theme>
  );
};